<template>
    <div>
        <div class="ui-list-bar">
            <ui-pills v-model="companyFilter" :items="companyPills" />
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <!-- Link de envio sem login (WhatsApp) -->
                <rma-share-link-button :competence="competence" :companies="companies" />
                <v-btn v-if="$allowed('rma.clarifications')" outlined @click="openClarify(null)">
                    <v-icon left small>mdi-comment-question-outline</v-icon>Pedir esclarecimento
                </v-btn>
                <v-btn v-if="$allowed('rma.request')" outlined :disabled="!reinformableItems.length" @click="openReinform">
                    <v-icon left small>mdi-file-undo-outline</v-icon>Reinformar pendências
                </v-btn>
                <v-btn v-if="$allowed('rma.review')" outlined :disabled="!acceptableItems.length" :loading="bulkAccepting" @click="acceptAllSent">
                    <v-icon left small>mdi-check-all</v-icon>Conferir enviados ({{ acceptableItems.length }})
                </v-btn>
                <v-btn outlined :disabled="!visibleFilesCount" :loading="busy === 'zip-competence'" @click="downloadCompetenceZip">
                    <v-icon left small>mdi-folder-zip-outline</v-icon>Baixar competência (.zip)
                </v-btn>
                <v-btn outlined :disabled="!items.length" @click="exportChecklist"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar checklist</v-btn>
            </div>
        </div>

        <section v-if="clarifications.length" class="ui-card rma-clar mb-4">
            <div class="rma-clar__header">
                <div>
                    <h3 class="ui-section-title">Esclarecimentos</h3>
                    <p class="ui-section-description">
                        {{ openClarifications }} aguardando resposta · {{ answeredClarifications }} respondido(s). A recuperanda responde pelo Painel da Devedora.
                    </p>
                </div>
            </div>
            <div v-for="row in clarifications" :key="row.id" class="rma-clar__row" :class="`rma-clar__row--${row.status}`">
                <div class="rma-clar__main">
                    <div class="rma-clar__meta">
                        <ui-badge :status="clarificationBadge(row)" :text="row.status_label" />
                        <span class="ui-cell-main">{{ row.document_name ? `${row.document_name}${companies.length > 1 && row.company_name ? ` · ${row.company_name}` : ""}` : "Geral" }}</span>
                        <span class="ui-cell-sub">pedido por {{ row.asked_by_name || "AJ" }} em {{ formatDateTime(row.asked_at) }}</span>
                    </div>
                    <div class="rma-clar__question">{{ row.question }}</div>
                    <div v-if="row.answer" class="rma-clar__answer">
                        <div class="ui-cell-sub mb-1">Resposta de {{ row.answered_by_name || "recuperanda" }}{{ row.answered_by_email ? ` (${row.answered_by_email})` : "" }} em {{ formatDateTime(row.answered_at) }}</div>
                        {{ row.answer }}
                    </div>
                </div>
                <v-btn v-if="(row.status !== 'closed') && $allowed('rma.clarifications')" small text :loading="busy === `clar-${row.id}`" @click="closeClarification(row)">Encerrar</v-btn>
            </div>
        </section>

        <ui-table-card>
            <v-data-table :headers="tableHeaders" :items="visibleItems" :loading="loading" disable-sort disable-pagination hide-default-footer
                item-key="id" show-expand :expanded.sync="expanded" no-data-text="Nenhum documento nesta competência" loading-text="Carregando..."
                class="rma-checklist">
                <template v-slot:[`item.document`]="{ item }">
                    <div class="ui-cell-main">{{ String(item.document_type.sort_order).padStart(2, "0") }} · {{ item.document_type.name }}</div>
                    <div class="ui-cell-sub">{{ formatExtensions(item.document_type.accepted_extensions) }}</div>
                </template>
                <template v-slot:[`item.company_name`]="{ item }"><span class="ui-small">{{ item.company_name }}</span></template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge set="rma-item" :status="item.status" />
                    <div v-if="hasProblem(item)" class="ui-cell-sub rma-review" :class="`rma-review--${item.review_status}`">
                        <v-icon x-small>mdi-alert-circle-outline</v-icon> {{ item.review_label }}{{ item.status === "returned" ? "" : " · sem cobrança" }}
                    </div>
                    <div v-if="item.sent_after_deadline && !item.missing" class="ui-cell-sub" style="color: #b45309;">enviado após o prazo</div>
                    <div v-if="itemClarification(item)" class="ui-cell-sub" style="color: #2b2050;">
                        <v-icon x-small>mdi-comment-question-outline</v-icon> {{ itemClarification(item).status === "open" ? "esclarecimento pendente" : "esclarecimento respondido" }}
                    </div>
                </template>
                <template v-slot:[`item.files`]="{ item }">
                    <span v-if="item.files.length" class="ui-nowrap"><v-icon small>mdi-paperclip</v-icon> {{ item.files.length }}</span>
                    <span v-else class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.last_sent_at`]="{ item }"><span class="ui-muted ui-nowrap">{{ item.last_sent_at ? formatDateTime(item.last_sent_at) : "—" }}</span></template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex align-center justify-end" style="gap: 4px;">
                        <v-btn v-if="canAccept(item)" small outlined :loading="busy === `accept-${item.id}`" @click.stop="accept(item)">Conferir</v-btn>
                        <ui-row-actions :actions="rowActions(item)" />
                    </div>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="rma-checklist__detail">
                        <div v-if="item.document_type.instructions" class="ui-small ui-muted mb-2">Orientação à recuperanda: {{ item.document_type.instructions }}</div>
                        <div v-if="item.return_message" class="ui-suggestion ui-suggestion--warn mb-3">
                            <strong>{{ returnTitle(item) }}:</strong>
                            {{ item.return_message }}
                        </div>
                        <div v-if="item.justification" class="ui-suggestion ui-suggestion--ok mb-3"><strong>Não se aplica:</strong> {{ item.justification }}</div>
                        <div v-if="item.files.length > 1" class="d-flex align-center mb-2">
                            <span class="ui-small ui-muted">{{ item.files.length }} arquivos nesta pasta</span>
                            <v-spacer></v-spacer>
                            <v-btn small text color="primary" :loading="busy === `zip-${item.id}`" @click="downloadItemZip(item)"><v-icon left small>mdi-folder-zip-outline</v-icon>Baixar todos (.zip)</v-btn>
                        </div>
                        <ul v-if="item.files.length" class="ui-checks">
                            <li v-for="file in item.files" :key="file.id">
                                <v-icon small>{{ fileIcon(file.original_name) }}</v-icon>
                                <div style="flex: 1; min-width: 0;">
                                    <div class="ui-cell-main text-truncate" :title="file.original_name">{{ file.original_name }}</div>
                                    <div class="ui-cell-sub">
                                        {{ formatBytes(file.size) }} · {{ formatDateTime(file.uploaded_at) }}
                                        · {{ file.uploaded_by_type === "admin" ? "enviado pela AJ" : "enviado pela recuperanda" }}
                                        <span v-if="file.uploaded_after_deadline" style="color: #b45309;"> · após o prazo</span>
                                    </div>
                                </div>
                                <v-btn small text color="primary" @click="download(file)"><v-icon left small>mdi-download</v-icon>Baixar</v-btn>
                                <v-btn v-if="(item.status !== 'accepted') && $allowed('rma.review')" small text class="ui-btn-danger" @click="removeFile(item, file)">Excluir</v-btn>
                            </li>
                        </ul>
                        <div v-else class="ui-small ui-muted">Nenhum arquivo enviado.</div>
                        <div v-if="uploads[item.id]" class="rma-upload mt-3" role="status" aria-live="polite">
                            <div class="rma-upload__head">
                                <span class="rma-upload__name" :title="uploads[item.id].name">
                                    {{ uploads[item.id].saving ? "Concluindo envio de" : "Enviando" }} {{ uploads[item.id].name }}<template
                                        v-if="uploads[item.id].count > 1"> ({{ uploads[item.id].index }} de {{ uploads[item.id].count }})</template>
                                </span>
                                <span class="rma-upload__percent">{{ uploads[item.id].percent }}%</span>
                            </div>
                            <v-progress-linear :value="uploads[item.id].percent" :indeterminate="uploads[item.id].saving" height="6" rounded
                                color="primary" background-color="#e2e8f0" :aria-label="`Progresso do envio de ${uploads[item.id].name}`"></v-progress-linear>
                            <div class="ui-cell-sub mt-1">{{ formatBytes(uploads[item.id].loaded) }} de {{ formatBytes(uploads[item.id].size) }}</div>
                        </div>
                    </td>
                </template>
            </v-data-table>
        </ui-table-card>

        <input ref="adminFiles" type="file" multiple class="d-none" @change="onAdminFiles" />

        <v-dialog v-model="clarifyDialog.open" max-width="580" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Pedir esclarecimento{{ competence ? ` · ${competence.label}` : "" }}</h3>
                        <p v-if="clarifyDialog.item" class="ui-cell-main mb-1">{{ clarifyDialog.item.document_type.name }} · {{ clarifyDialog.item.company_name }}</p>
                        <p v-else class="ui-cell-main mb-1">Pergunta geral sobre a competência</p>
                        <p class="ui-section-description">A recuperanda recebe o pedido por e-mail e responde pelo Painel da Devedora. Você e o responsável pelo RMA são avisados quando ela responder.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-textarea v-model="clarifyDialog.question" label="O que precisa ser esclarecido" outlined rows="4" auto-grow hide-details="auto" counter="4000" maxlength="4000"
                        placeholder="Ex.: O saldo de caixa do balancete não confere com o extrato do Banrisul. Explique a diferença."></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="clarifyDialog.saving" @click="clarifyDialog.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('rma.clarifications')" color="primary" depressed :loading="clarifyDialog.saving" :disabled="clarifyDialog.question.trim().length < 5" @click="confirmClarify">
                        <v-icon left small>mdi-send-outline</v-icon>Enviar pedido
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="reinformDialog.open" max-width="620" persistent scrollable>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Reinformar pendências{{ competence ? ` · ${competence.label}` : "" }}</h3>
                        <p class="ui-section-description">
                            Marque os documentos dados como entregues que continuam pendentes. Eles voltam a ser cobrados, entram nas cobranças das competências
                            seguintes e a recuperanda recebe um único e-mail com a lista e o motivo.
                        </p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div v-if="competence && competence.collection_stopped_at" class="ui-suggestion ui-suggestion--warn mb-3">
                        A cobrança desta competência está encerrada e será retomada.
                    </div>
                    <div class="d-flex align-center mb-1">
                        <div class="ui-label mb-0">Documentos ({{ reinformDialog.ids.length }} de {{ reinformableItems.length }})</div>
                        <v-spacer></v-spacer>
                        <v-btn small text @click="toggleAllReinform">{{ reinformDialog.ids.length === reinformableItems.length ? "Desmarcar todos" : "Marcar todos" }}</v-btn>
                    </div>
                    <div class="rma-reinform__list mb-4">
                        <v-checkbox v-for="item in reinformableItems" :key="`reinform-${item.id}`" v-model="reinformDialog.ids" :value="item.id" dense hide-details class="mt-0 mb-1">
                            <template #label>
                                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                                    <span>{{ String(item.document_type.sort_order).padStart(2, "0") }} · {{ item.document_type.name }}<span v-if="companies.length > 1" class="ui-muted"> · {{ item.company_name }}</span></span>
                                    <ui-badge set="rma-item" :status="item.status" />
                                </div>
                            </template>
                        </v-checkbox>
                    </div>
                    <v-textarea v-model="reinformDialog.message" label="Motivo (vai no e-mail)" outlined rows="3" auto-grow hide-details="auto" counter="2000" maxlength="2000"
                        placeholder="Ex.: Os extratos enviados não incluem a conta do Banrisul. Envie os extratos de todas as contas."></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="reinformDialog.saving" @click="reinformDialog.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('rma.request')" color="primary" depressed :loading="reinformDialog.saving" :disabled="!reinformDialog.ids.length || reinformDialog.message.trim().length < 5" @click="confirmReinform">
                        Reinformar e avisar ({{ reinformDialog.ids.length }})
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="reviewDialog.open" max-width="580" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Avaliar documento</h3>
                        <p v-if="reviewDialog.item" class="ui-cell-main mb-1">{{ reviewDialog.item.document_type.name }} · {{ reviewDialog.item.company_name }}</p>
                        <p class="ui-section-description">Indique se o que foi enviado está aprovado, incompleto ou rejeitado.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <div class="rma-review-options mb-4">
                        <button v-for="option in reviewOptions" :key="option.value" type="button" class="rma-review-option"
                            :class="[`rma-review-option--${option.value}`, { 'rma-review-option--active': reviewDialog.review === option.value }]"
                            @click="reviewDialog.review = option.value">
                            <v-icon small>{{ option.icon }}</v-icon>
                            <span class="rma-review-option__title">{{ option.text }}</span>
                            <span class="rma-review-option__hint">{{ option.hint }}</span>
                        </button>
                    </div>
                    <template v-if="reviewDialog.review !== 'ok'">
                        <v-textarea v-model="reviewDialog.message" :label="reviewDialog.review === 'wrong' ? 'Motivo da rejeição' : 'O que está faltando'" outlined rows="3" auto-grow
                            hide-details="auto" counter="2000" maxlength="2000" placeholder="Ex.: Os extratos do C6 estão protegidos por senha. Envie os PDFs sem senha."></v-textarea>
                        <v-checkbox v-model="reviewDialog.charge" dense hide-details class="mt-2" label="Cobrar a devedora novamente"></v-checkbox>
                        <p class="ui-small ui-muted mt-1 mb-0">
                            {{ reviewDialog.charge
                                ? "O documento volta a pendente, entra nas cobranças e a recuperanda recebe a avaliação e o motivo por e-mail e no painel."
                                : "Fica registrado aqui e no histórico. A recuperanda não é avisada e o documento continua como entregue." }}
                        </p>
                    </template>
                    <p v-else class="ui-small ui-muted mb-0">O documento fica aprovado (conferido).</p>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="reviewDialog.saving" @click="reviewDialog.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('rma.review')" :color="reviewDialog.review === 'ok' ? 'primary' : 'error'" depressed :loading="reviewDialog.saving"
                        :disabled="reviewDialog.review !== 'ok' && reviewDialog.message.trim().length < 5" @click="confirmReview">
                        {{ reviewDialog.review === "ok" ? "Aprovar" : reviewDialog.charge ? "Salvar e cobrar" : "Salvar avaliação" }}
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import RmaShareLinkButton from "@/components/rma/RmaShareLinkButton.vue";
import { ITEM_STATUS_LABELS, REVIEW_LABELS, formatBytes, formatDateTime, formatExtensions, notifyError, notifySuccess, notifyWarning, openUrl, uploadFile } from "@/components/rma/rma.util";

const MAX_FILE_BYTES = 200 * 1024 * 1024;

// Checklist da competencia no backoffice (aba Documentos do caso): o squad
// confere, devolve com motivo e envia arquivos recebidos por outro canal.
export default {
    name: "RmaChecklist",

    components: { RmaShareLinkButton },

    props: {
        competenceId: { type: Number, required: true },
    },

    data: () => ({
        loading: false,
        competence: null,
        companies: [],
        items: [],
        companyFilter: "all",
        expanded: [],
        busy: null,
        bulkAccepting: false,
        uploadTarget: null,
        uploads: {},
        reviewDialog: { open: false, item: null, review: "incomplete", message: "", charge: true, saving: false },
        reviewOptions: [
            { value: "ok", text: "Aprovado", icon: "mdi-check-circle-outline", hint: "Documento correto" },
            { value: "incomplete", text: "Incompleto", icon: "mdi-progress-alert", hint: "Falta parte do conteúdo" },
            { value: "wrong", text: "Rejeitado", icon: "mdi-close-circle-outline", hint: "Documento incorreto" },
        ],
        reinformDialog: { open: false, ids: [], message: "", saving: false },
        clarifications: [],
        clarifyDialog: { open: false, item: null, question: "", saving: false },
    }),

    computed: {
        companyPills() {
            const missing = (companyId) => this.items.filter((item) => item.missing && (companyId === "all" || item.company_id === companyId)).length;
            return [{ value: "all", text: "Todas as empresas", count: this.items.length }].concat(this.companies.map((company) => ({
                value: company.id,
                text: company.name,
                tone: missing(company.id) ? "warning" : "success",
                count: company.counts.total,
            })));
        },
        visibleItems() {
            return this.companyFilter === "all" ? this.items : this.items.filter((item) => item.company_id === this.companyFilter);
        },
        tableHeaders() {
            const headers = [
                { text: "Documento", value: "document" },
                { text: "Empresa", value: "company_name", width: 220 },
                { text: "Situação", value: "status", width: 160 },
                { text: "Arquivos", value: "files", width: 90 },
                { text: "Último envio", value: "last_sent_at", width: 150 },
                { text: "", value: "actions", align: "end", width: 140 },
                { text: "", value: "data-table-expand", width: 40 },
            ];
            return this.companyFilter === "all" && this.companies.length > 1 ? headers : headers.filter((header) => header.value !== "company_name");
        },
        acceptableItems() {
            return this.visibleItems.filter((item) => item.status === "sent");
        },
        openClarifications() {
            return this.clarifications.filter((row) => row.status === "open").length;
        },
        answeredClarifications() {
            return this.clarifications.filter((row) => row.status === "answered").length;
        },
        visibleFilesCount() {
            return this.visibleItems.reduce((total, item) => total + item.files.length, 0);
        },
        // Itens dados como entregues (enviado, conferido ou nao se aplica).
        reinformableItems() {
            return this.visibleItems.filter((item) => ["sent", "not_applicable", "accepted"].includes(item.status));
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatBytes,
        formatDateTime,
        formatExtensions,

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/rma/competences/${this.competenceId}`);
                this.applyChecklist(result.data.content);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        applyChecklist(content) {
            this.competence = content.competence;
            this.companies = content.companies;
            this.items = content.items;
            this.clarifications = content.clarifications || [];
            if (this.companies.length === 1) this.companyFilter = this.companies[0].id;
            this.$emit("changed", content.competence);
        },

        // Cada acao devolve o item e a competencia; a tela troca so a linha.
        applyItem(content) {
            const index = this.items.findIndex((item) => item.id === content.item.id);
            if (index >= 0) this.$set(this.items, index, content.item);
            this.competence = content.competence;
            this.refreshCompanyCounts();
            this.$emit("changed", content.competence);
        },

        refreshCompanyCounts() {
            this.companies = this.companies.map((company) => {
                const statuses = this.items.filter((item) => item.company_id === company.id).map((item) => item.status);
                const missing = statuses.filter((status) => status === "pending" || status === "returned").length;
                return { ...company, counts: { ...company.counts, total: statuses.length, missing, delivered: statuses.length - missing } };
            });
        },

        canAccept(item) {
            return item.status === "sent" || item.status === "not_applicable";
        },

        rowActions(item) {
            return [
                { text: "Conferir", icon: "mdi-check", handler: () => this.accept(item), hidden: !this.canAccept(item) || !this.$allowed("rma.review") },
                { text: "Pedir esclarecimento", icon: "mdi-comment-question-outline", handler: () => this.openClarify(item), hidden: !this.$allowed("rma.clarifications") },
                { text: "Avaliar documento", icon: "mdi-clipboard-check-outline", handler: () => this.openReview(item), hidden: !["sent", "not_applicable", "accepted"].includes(item.status) || !this.$allowed("rma.review") },
                { text: "Desfazer conferência", icon: "mdi-backup-restore", handler: () => this.reopen(item), hidden: item.status !== "accepted" || !this.$allowed("rma.review") },
                { text: "Enviar arquivo pela AJ", icon: "mdi-upload", handler: () => this.pickAdminFiles(item), hidden: item.status === "accepted" || !this.$allowed("rma.review") },
                { text: "Ver arquivos", icon: "mdi-paperclip", handler: () => this.toggleExpanded(item), hidden: !item.files.length },
                { text: "Baixar todos (.zip)", icon: "mdi-folder-zip-outline", handler: () => this.downloadItemZip(item), hidden: item.files.length < 2 },
            ];
        },

        toggleExpanded(item) {
            this.expanded = this.expanded.some((row) => row.id === item.id) ? this.expanded.filter((row) => row.id !== item.id) : [...this.expanded, item];
        },

        async accept(item) {
            this.busy = `accept-${item.id}`;
            try {
                const result = await axios.post(`/api/admin/rma/items/${item.id}/accept`);
                this.applyItem(result.data.content);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },

        async acceptAllSent() {
            const targets = [...this.acceptableItems];
            const confirmed = await this.$dialog.confirm({
                title: "Conferir documentos enviados",
                text: `Marcar como conferidos os ${targets.length} documentos enviados${this.companyFilter === "all" ? "" : " desta empresa"}? Itens "não se aplica" continuam para conferência individual.`,
                actions: { false: "Cancelar", true: { text: "Conferir", color: "primary" } },
            });
            if (!confirmed) return;
            this.bulkAccepting = true;
            try {
                for (const item of targets) {
                    const result = await axios.post(`/api/admin/rma/items/${item.id}/accept`);
                    this.applyItem(result.data.content);
                }
                notifySuccess(this, `${targets.length} documento(s) conferido(s).`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.bulkAccepting = false;
            }
        },

        hasProblem(item) {
            return ["incomplete", "wrong"].includes(item.review_status);
        },

        returnTitle(item) {
            const review = this.hasProblem(item) ? ` como ${REVIEW_LABELS[item.review_status].toLowerCase()}` : "";
            if (item.status === "returned") return `Devolvido${review}${item.returned_at ? ` em ${formatDateTime(item.returned_at)}` : ""}`;
            if (this.hasProblem(item)) return `Avaliado${review} (sem nova cobrança)${item.reviewed_at ? ` em ${formatDateTime(item.reviewed_at)}` : ""}`;
            return `Última devolução${item.returned_at ? ` em ${formatDateTime(item.returned_at)}` : ""}`;
        },

        openReview(item) {
            const review = this.hasProblem(item) ? item.review_status : "incomplete";
            this.reviewDialog = { open: true, item, review, message: "", charge: true, saving: false };
        },

        async confirmReview() {
            const { item, review, charge } = this.reviewDialog;
            this.reviewDialog.saving = true;
            try {
                const result = await axios.post(`/api/admin/rma/items/${item.id}/review`, { review, charge, message: this.reviewDialog.message.trim() });
                this.applyItem(result.data.content);
                this.reviewDialog.open = false;
                if (review === "ok") notifySuccess(this, "Documento aprovado.");
                else if (charge) notifySuccess(this, `Documento avaliado como ${REVIEW_LABELS[review].toLowerCase()}. A recuperanda foi avisada por e-mail.`);
                else notifySuccess(this, `Avaliação registrada (${REVIEW_LABELS[review].toLowerCase()}, sem nova cobrança).`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.reviewDialog.saving = false;
            }
        },

        itemClarification(item) {
            return this.clarifications.find((row) => Number(row.item_id) === Number(item.id) && row.status !== "closed") || null;
        },

        clarificationBadge(row) {
            if (row.status === "open") return "pending";
            if (row.status === "answered") return "active";
            return "inactive";
        },

        openClarify(item) {
            this.clarifyDialog = { open: true, item, question: "", saving: false };
        },

        async confirmClarify() {
            this.clarifyDialog.saving = true;
            try {
                const payload = { question: this.clarifyDialog.question.trim() };
                if (this.clarifyDialog.item) payload.item_id = this.clarifyDialog.item.id;
                const result = await axios.post(`/api/admin/rma/competences/${this.competenceId}/clarifications`, payload);
                const content = result.data.content;
                this.clarifications = [content.clarification, ...this.clarifications];
                this.clarifyDialog.open = false;
                const email = content.email || {};
                if (email.no_recipients) notifyWarning(this, "Pedido registrado, mas não há usuário da recuperanda para receber o e-mail.");
                else if (!email.sent) notifyWarning(this, "Pedido registrado, mas o e-mail não foi entregue. Veja no Histórico.");
                else notifySuccess(this, "Pedido de esclarecimento enviado à recuperanda.");
                this.$emit("changed", this.competence);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.clarifyDialog.saving = false;
            }
        },

        async closeClarification(row) {
            this.busy = `clar-${row.id}`;
            try {
                const result = await axios.post(`/api/admin/rma/clarifications/${row.id}/close`);
                const index = this.clarifications.findIndex((entry) => entry.id === row.id);
                if (index >= 0) this.$set(this.clarifications, index, result.data.content);
                notifySuccess(this, "Esclarecimento encerrado.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },

        openReinform() {
            this.reinformDialog = { open: true, ids: [], message: "", saving: false };
        },

        toggleAllReinform() {
            this.reinformDialog.ids = this.reinformDialog.ids.length === this.reinformableItems.length ? [] : this.reinformableItems.map((item) => item.id);
        },

        async confirmReinform() {
            this.reinformDialog.saving = true;
            try {
                const result = await axios.post(`/api/admin/rma/competences/${this.competenceId}/reinform`, {
                    item_ids: this.reinformDialog.ids,
                    message: this.reinformDialog.message.trim(),
                });
                const content = result.data.content;
                this.applyChecklist(content);
                this.reinformDialog.open = false;
                const resumed = content.collection_resumed ? " A cobrança foi retomada." : "";
                if (content.email && content.email.no_recipients) notifyWarning(this, `${content.reinformed} documento(s) voltaram a pendentes, mas não há usuário da recuperanda para receber o e-mail.${resumed}`);
                else if (!content.email || !content.email.sent) notifyWarning(this, `${content.reinformed} documento(s) voltaram a pendentes, mas o e-mail não foi entregue. Veja no Histórico.${resumed}`);
                else notifySuccess(this, `${content.reinformed} documento(s) voltaram a pendentes. A recuperanda foi avisada por e-mail.${resumed}`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.reinformDialog.saving = false;
            }
        },

        async reopen(item) {
            try {
                const result = await axios.post(`/api/admin/rma/items/${item.id}/reopen`);
                this.applyItem(result.data.content);
            } catch (error) {
                notifyError(this, error);
            }
        },

        pickAdminFiles(item) {
            this.uploadTarget = item;
            const input = this.$refs.adminFiles;
            input.accept = item.document_type.accepted_extensions.map((ext) => `.${ext}`).join(",");
            input.click();
        },

        // R10: documento recebido por outro canal, enviado pela AJ em nome da recuperanda.
        async onAdminFiles(event) {
            const files = Array.from(event.target.files || []);
            event.target.value = "";
            const item = this.uploadTarget;
            if (!item || !files.length) return;
            if (!this.expanded.some((row) => row.id === item.id)) this.expanded = [...this.expanded, item];

            const accepted = item.document_type.accepted_extensions;
            for (const [index, file] of files.entries()) {
                const extension = (file.name.split(".").pop() || "").toLowerCase();
                if (!accepted.includes(extension)) {
                    notifyWarning(this, `"${file.name}": formato não aceito. Envie ${formatExtensions(accepted)}.`);
                    continue;
                }
                if (file.size > MAX_FILE_BYTES) {
                    notifyWarning(this, `"${file.name}" passa de 200 MB.`);
                    continue;
                }
                try {
                    // A API recebe o arquivo e repassa ao storage; com 100% enviado, falta ela concluir.
                    const progress = { name: file.name, index: index + 1, count: files.length, size: file.size, loaded: 0, percent: 0, saving: false };
                    this.$set(this.uploads, item.id, progress);
                    const result = await uploadFile(`/api/admin/rma/items/${item.id}/files`, file, {
                        onProgress: ({ loaded, percent }) => {
                            progress.loaded = loaded;
                            progress.percent = percent;
                            progress.saving = percent >= 100;
                        },
                    });
                    this.applyItem(result.data.content);
                } catch (error) {
                    notifyError(this, error);
                }
            }
            this.$delete(this.uploads, item.id);
        },

        // .zip de uma pasta: um documento (arquivos na raiz) ou a competencia (pastas por empresa e tipo).
        async downloadZip(params, busyKey) {
            this.busy = busyKey;
            try {
                const result = await axios.get(`/api/admin/rma/clients/${this.competence.client_id}/archive`, { params, responseType: "blob" });
                const disposition = result.headers["content-disposition"] || "";
                const match = disposition.match(/filename="([^"]+)"/);
                const url = window.URL.createObjectURL(result.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = match ? match[1] : `RMA_${this.competence.key}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                // Erro de API chega como Blob quando responseType = blob.
                if (error.response && error.response.data instanceof Blob) {
                    try {
                        error.response.data = JSON.parse(await error.response.data.text());
                    } catch (parseError) {
                        // mantem o erro original
                    }
                }
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },

        downloadItemZip(item) {
            return this.downloadZip({ item_id: item.id }, `zip-${item.id}`);
        },

        downloadCompetenceZip() {
            const params = { from: this.competence.key, to: this.competence.key };
            if (this.companyFilter !== "all") params.company_ids = String(this.companyFilter);
            return this.downloadZip(params, "zip-competence");
        },

        async download(file) {
            try {
                const result = await axios.get(`/api/admin/rma/files/${file.id}/download`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },

        async removeFile(item, file) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir arquivo",
                text: `"${file.original_name}" sai do checklist. O registro do envio continua no histórico. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                const result = await axios.delete(`/api/admin/rma/files/${file.id}`);
                this.applyItem(result.data.content);
            } catch (error) {
                notifyError(this, error);
            }
        },

        fileIcon(name) {
            const extension = String(name || "").split(".").pop().toLowerCase();
            if (extension === "pdf") return "mdi-file-pdf-box";
            if (["xls", "xlsx", "csv"].includes(extension)) return "mdi-file-excel-outline";
            if (["png", "jpg", "jpeg"].includes(extension)) return "mdi-file-image-outline";
            return "mdi-file-outline";
        },

        // Uma linha por arquivo (e uma por item sem arquivo).
        exportChecklist() {
            const rows = [];
            this.items.forEach((item) => {
                const company = this.companies.find((entry) => entry.id === item.company_id) || {};
                const base = {
                    Competência: this.competence ? this.competence.label : "",
                    Empresa: item.company_name,
                    CNPJ: company.registration || "",
                    Documento: item.document_type.name,
                    Situação: ITEM_STATUS_LABELS[item.status] || item.status,
                    Avaliação: REVIEW_LABELS[item.review_status] || "",
                    "Enviado após o prazo": item.sent_after_deadline ? "Sim" : "Não",
                    Justificativa: item.justification || "",
                    "Motivo da devolução": item.return_message || "",
                    "Conferido em": item.accepted_at ? formatDateTime(item.accepted_at) : "",
                };
                if (!item.files.length) rows.push({ ...base, Arquivo: "", "Enviado em": "", "Enviado por": "", Tamanho: "" });
                item.files.forEach((file) => rows.push({
                    ...base,
                    Arquivo: file.original_name,
                    "Enviado em": formatDateTime(file.uploaded_at),
                    "Enviado por": file.uploaded_by_type === "admin" ? "AJ" : "Recuperanda",
                    Tamanho: formatBytes(file.size),
                }));
            });
            downloadXlsx(rows, `Checklist_RMA_${this.competence ? this.competence.key : ""}`);
        },
    },
};
</script>

<style scoped>
.rma-upload {
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    max-width: 560px;
}

.rma-upload__head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;
}

.rma-upload__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rma-upload__percent {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.rma-review {
    display: flex;
    align-items: center;
    gap: 3px;
    font-weight: 600;
}

.rma-review--incomplete,
.rma-review--incomplete .v-icon {
    color: #b45309 !important;
}

.rma-review--wrong,
.rma-review--wrong .v-icon {
    color: #b42318 !important;
}

.rma-review-options {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.rma-review-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px 14px;
    border: 1px solid #e3e1ea;
    border-radius: 10px;
    background: #fff;
    text-align: left;
    transition: border-color .15s ease, background-color .15s ease;
}

.rma-review-option:hover {
    border-color: #c9c4d8;
}

.rma-review-option__title {
    font-weight: 600;
    font-size: 14px;
}

.rma-review-option__hint {
    font-size: 12px;
    color: #6b6880;
}

.rma-review-option--ok.rma-review-option--active {
    border-color: #15803d;
    background: #f0fdf4;
}

.rma-review-option--incomplete.rma-review-option--active {
    border-color: #b45309;
    background: #fffbeb;
}

.rma-review-option--wrong.rma-review-option--active {
    border-color: #b42318;
    background: #fef3f2;
}

.rma-review-option--ok .v-icon { color: #15803d; }
.rma-review-option--incomplete .v-icon { color: #b45309; }
.rma-review-option--wrong .v-icon { color: #b42318; }

@media (max-width: 600px) {
    .rma-review-options {
        grid-template-columns: 1fr;
    }
}

.rma-clar {
    padding: 0;
}

.rma-clar__header {
    padding: 14px 18px 10px;
}

.rma-clar__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 18px;
    border-top: 1px solid #eef0f3;
}

.rma-clar__row--closed {
    opacity: 0.7;
}

.rma-clar__main {
    flex: 1;
    min-width: 0;
}

.rma-clar__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
}

.rma-clar__question {
    margin-top: 6px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f6f4fb;
    font-size: 13.5px;
    white-space: pre-line;
}

.rma-clar__answer {
    margin-top: 6px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f0fdf4;
    font-size: 13.5px;
    white-space: pre-line;
}

.rma-reinform__list {
    max-height: 320px;
    overflow-y: auto;
}

.rma-checklist__detail {
    background: #fafafa;
    padding: 14px 18px !important;
}
</style>
